<template>
  <div class="a2p-wizard">
    <div class="a2p-shell">
      <!-- Sidebar: branding + progress + section nav -->
      <aside class="a2p-sidebar">
        <div class="a2p-brand">
          <div class="a2p-brand-mark">{{ brandMark }}</div>
          <div>
            <div class="a2p-brand-name">{{ effectiveSiteName }}</div>
            <div class="a2p-brand-sub">SMS Onboarding</div>
          </div>
        </div>

        <div class="a2p-progress-card">
          <div class="a2p-progress-label">
            <span>Your progress</span>
            <span class="pct">{{ step + 1 }} / {{ stepCount }}</span>
          </div>
          <div class="a2p-progress-bar">
            <div class="a2p-progress-fill" :style="{ width: progressPercent + '%' }" />
          </div>
          <div v-if="!submitted" class="a2p-progress-time">
            <span class="dot" />
            <span>{{ minutesRemaining > 0 ? `~${minutesRemaining} min remaining` : 'Ready to submit' }}</span>
          </div>
        </div>

        <nav class="a2p-nav">
          <button
            v-for="(label, i) in navLabels"
            :key="i"
            type="button"
            class="a2p-nav-item"
            :class="{ active: step === i, done: i < step }"
            :disabled="!canJumpTo(i)"
            @click="jumpToStep(i)"
          >
            <div class="a2p-nav-num">{{ navNumFor(i) }}</div>
            <span>{{ label }}</span>
          </button>
        </nav>

        <div class="a2p-quick-ref">
          <div class="a2p-quick-ref-label">Quick reference</div>
          <p>
            A2P 10DLC is a US/Canada federal requirement for businesses texting
            customers. We handle the carrier registration — you provide the inputs.
          </p>
        </div>
      </aside>

      <!-- Main content area — renders the current step -->
      <main class="a2p-main-wrap">
        <component
          :is="currentStepComponent"
          v-bind="stepProps"
          @next="handleNext"
          @back="handleBack"
          @close="handleClose"
          @refresh="handleRefresh"
          @import-data="handleImportData"
          @start-over="handleStartOver"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import mixin from '@/mixin';
import { getRegStatus, regToForm } from '@/utils/a2pRegistration';
import A2PWizardIntro from './A2PWizardIntro.vue';
import A2PWizardBusinessInfo from './A2PWizardBusinessInfo.vue';
import A2PWizardRepresentative from './A2PWizardRepresentative.vue';
import A2PWizardBrandUseCase from './A2PWizardBrandUseCase.vue';
import A2PWizardWebsiteCompliance from './A2PWizardWebsiteCompliance.vue';
import A2PWizardReview from './A2PWizardReview.vue';
import A2PWizardStatus from './A2PWizardStatus.vue';
import A2PWizardPhoneNumbers from './A2PWizardPhoneNumbers.vue';
import A2PWizardComplete from './A2PWizardComplete.vue';

// Single source of truth for the wizard flow. Order is the flow; keys are how the
// rest of the file references steps (never raw indices). Pre-submit steps are
// intro..compliance; `review` submits; status/phones/complete are post-submit
// and stay sidebar-locked. Adding/removing a step here ripples everywhere via
// stepIndex()/currentStepKey — and the mirror test in
// cypress/node-tests/a2p-wizard-steps.test.js must be updated in the same change.
const STEPS = [
  { key: 'intro',          label: 'Welcome',                   component: 'A2PWizardIntro' },
  { key: 'business',       label: 'Business information',      component: 'A2PWizardBusinessInfo' },
  { key: 'representative', label: 'Authorized representative', component: 'A2PWizardRepresentative' },
  { key: 'brand',          label: 'Brand & use case',          component: 'A2PWizardBrandUseCase' },
  { key: 'compliance',     label: 'Website compliance',        component: 'A2PWizardWebsiteCompliance' },
  { key: 'review',         label: 'Review & submit',           component: 'A2PWizardReview' },
  { key: 'status',         label: 'Registration status',       component: 'A2PWizardStatus' },
  { key: 'phones',         label: 'Phone numbers',             component: 'A2PWizardPhoneNumbers' },
  { key: 'complete',       label: 'Complete',                  component: 'A2PWizardComplete' },
];

export default {
  name: 'A2PRegistrationWizard',
  mixins: [mixin],
  props: {
    // Super Admin can override the siteID
    targetSiteID: {
      type: String,
      default: null,
    },
    targetSiteName: {
      type: String,
      default: null,
    },
    // Country from sites table (for Super Admin to pre-populate)
    targetSiteCountry: {
      type: String,
      default: null,
    },
  },
  components: {
    A2PWizardIntro,
    A2PWizardBusinessInfo,
    A2PWizardRepresentative,
    A2PWizardBrandUseCase,
    A2PWizardWebsiteCompliance,
    A2PWizardReview,
    A2PWizardStatus,
    A2PWizardPhoneNumbers,
    A2PWizardComplete,
  },
  data() {
    return {
      step: 0,
      form: {
        businessName: '',
        businessAddress: '',
        ein: '',
        businessType: '',
        website: '',
        country: 'US',
        repName: '',
        repTitle: '',
        repEmail: '',
        repPhone: '',
        brandDescription: '',
        sampleMessage: '',
        optInOut: '',
        privacyPolicyURL: '',
        termsOfServiceURL: '',
        optInScreenshotURL: '',
        messageFrequency: 'Up to 8 msgs/month',
        officePhone: '',
        officeEmail: '',
      },
      status: null,
      submitting: false,
      twilioPhoneNumbers: [],
      purchasedNumbers: [],
      storageKey: null,
    };
  },
  computed: {
    ...mapGetters('a2p', [
      'isLoading',
      'hasError',
      'errorMessage',
      'currentRegistration',
      'registrationStatus',
      'isRegistrationComplete',
      'isRegistrationPending',
    ]),

    effectiveSiteID() {
      return this.targetSiteID || this.siteID;
    },

    /**
     * True when we're filing on behalf of a DIFFERENT practice than the one the
     * operator is logged into (super-admin modal, or `?siteID=` on the route).
     *
     * This wizard began life as a customer-facing self-service flow, so several
     * steps still reach for ambient logged-in-site state (`allSettings`,
     * `customer.siteName`) to pre-fill "your practice's" details. That is correct
     * for a customer filing their own registration and WRONG here: it silently
     * writes the operator's site data into another practice's federal carrier
     * registration — and into the compliance text that practice then publishes
     * on its own website. Verified 2026-08-26: opening the wizard for Summerbrook
     * Dental (Fort Worth, TX) while logged into Mint Dental of Emerson pre-filled
     * Office Phone with Mint's `GENERAL_PHONE` (+1 201-834-7259, New Jersey).
     */
    isCrossSite() {
      return !!(this.targetSiteID && this.targetSiteID !== this.siteID);
    },

    /**
     * The practice name to use in generated TCR copy. Never falls back to the
     * operator's own site name while cross-site — an empty string is safer than
     * the wrong practice, and the child renders a visible placeholder for it.
     *
     * Same-site fallback reads the store's per-site getter (`customer/getSite`,
     * mapped in the mixin). There is no `customer.siteName` in the store — the
     * old child component read that key and always fell through to a placeholder.
     *
     * 2026-08-27: this computed was briefly defined TWICE (the cross-site guard
     * added above the 2025 original). The last key wins in an object literal, so
     * the guarded version was dead and the leak fix never ran. Keep exactly one —
     * `a2p-wizard-quick-import.test.js` now asserts it.
     */
    effectiveSiteName() {
      if (this.isCrossSite) return this.targetSiteName || '';
      return this.targetSiteName || this.getSite(this.siteID)?.Name || '';
    },

    brandMark() {
      const n = (this.effectiveSiteName || 'T').trim();
      return n.charAt(0).toUpperCase();
    },

    submitted() {
      return this.step >= this.stepIndex('status');
    },

    stepCount() {
      return STEPS.length;
    },

    progressPercent() {
      // 1-based, consistent with the sidebar's "{{ step + 1 }} / {{ stepCount }}".
      return Math.round(((this.step + 1) / STEPS.length) * 100);
    },

    navLabels() {
      return STEPS.map((s) => s.label);
    },

    minutesRemaining() {
      // Simple heuristic: 2 min per remaining pre-submit step (intro..compliance).
      const lastPreSubmit = this.stepIndex('compliance');
      if (this.step > lastPreSubmit) return 0;
      return Math.max(0, (lastPreSubmit - this.step) * 2);
    },

    currentStepKey() {
      return (STEPS[this.step] || STEPS[0]).key;
    },

    currentStepComponent() {
      return (STEPS[this.step] || STEPS[0]).component;
    },

    eyebrow() {
      // The welcome screen gets a friendly label; every other step shows its
      // 1-based position, matching the sidebar progress counter.
      if (this.currentStepKey === 'intro') return 'Welcome';
      return `Step ${this.step + 1} of ${STEPS.length}`;
    },

    stepProps() {
      const base = { eyebrow: this.eyebrow };
      switch (this.currentStepKey) {
        case 'intro':
          return {
            ...base,
            siteCountry: this.form.country,
            siteCountryFromDB: this.targetSiteCountry,
          };
        case 'business':
          return {
            ...base,
            // Staff operating on ANOTHER practice's registration must not get
            // ambient defaults sourced from their own logged-in site. See
            // `isCrossSite` below.
            isCrossSite: this.isCrossSite,
            businessName: this.form.businessName,
            businessAddress: this.form.businessAddress,
            ein: this.form.ein,
            businessType: this.form.businessType,
            website: this.form.website,
            country: this.form.country,
            privacyPolicyURL: this.form.privacyPolicyURL,
            termsOfServiceURL: this.form.termsOfServiceURL,
            optInScreenshotURL: this.form.optInScreenshotURL,
            messageFrequency: this.form.messageFrequency,
            officePhone: this.form.officePhone,
            officeEmail: this.form.officeEmail,
          };
        case 'representative':
          return {
            ...base,
            repName: this.form.repName,
            repTitle: this.form.repTitle,
            repEmail: this.form.repEmail,
            repPhone: this.form.repPhone,
          };
        case 'brand':
          return {
            ...base,
            brandDescription: this.form.brandDescription,
            sampleMessage: this.form.sampleMessage,
            optInOut: this.form.optInOut,
            officePhone: this.form.officePhone,
            messageFrequency: this.form.messageFrequency,
            // The generated TCR copy is built from THIS registration's data, not
            // from the operator's ambient site. Passing these explicitly is what
            // stops one practice's identity leaking into another's brand
            // description (see `effectiveSiteName`).
            practiceName: this.effectiveSiteName,
            businessName: this.form.businessName,
            businessAddress: this.form.businessAddress,
            repName: this.form.repName,
          };
        case 'compliance':
          return {
            ...base,
            officePhone: this.form.officePhone,
            officeEmail: this.form.officeEmail,
            messageFrequency: this.form.messageFrequency,
          };
        case 'review':
          return {
            ...base,
            ...this.form,
            submitting: this.submitting,
            siteID: this.effectiveSiteID,
          };
        case 'status':
          return {
            ...base,
            status: this.status || this.registrationStatus,
            registration: this.currentRegistration,
            error: this.hasError ? this.errorMessage : null,
            statusHistory: this.currentRegistration?.statusHistory || [],
            progress: this.currentRegistration?.progress,
            timeline: this.currentRegistration?.timeline,
            lastStatusUpdate: this.currentRegistration?.lastStatusUpdate,
            latestStatusDetails: this.currentRegistration?.latestStatusDetails,
          };
        case 'phones':
          return { ...base, existingNumbers: this.twilioPhoneNumbers };
        case 'complete':
          return {
            ...base,
            registrationStatus: this.status || this.registrationStatus,
            phoneNumberCount: this.twilioPhoneNumbers.length + this.purchasedNumbers.length,
            purchasedNumbers: this.purchasedNumbers,
          };
        default:
          return base;
      }
    },
  },
  async created() {
    // v2 key: the old 10-step layout persisted step indices that no longer line up
    // (documents step removed). Bump the key and drop any stale v1 entry so an old
    // saved index can never resume into the wrong step.
    this.storageKey = `a2p_wizard_v2_${this.effectiveSiteID}`;
    try { localStorage.removeItem(`a2p_wizard_${this.effectiveSiteID}`); } catch (e) { /* ignore */ }

    if (this.targetSiteCountry) {
      this.form.country = this.targetSiteCountry;
    }

    this.clearRegistration();

    try {
      await this.getA2PRegistrationStatus({ targetSiteID: this.targetSiteID });
      if (this.currentRegistration) {
        const reg = this.currentRegistration;
        const regStatus = getRegStatus(reg);
        this.status = regStatus;

        this.form = regToForm(reg);

        if (regStatus === 'approved') {
          await this.loadPhoneNumbers();
          if (this.twilioPhoneNumbers.length > 0) {
            this.step = this.stepIndex('complete');
          } else {
            this.step = this.stepIndex('phones');
          }
        } else {
          this.step = this.stepIndex('status');
        }
        return;
      }
    } catch (error) {
      console.log('No existing registration found, starting fresh:', error);
    }

    const hasProgress = this.loadProgress();
    if (hasProgress && this.step > 0) {
      if (this.step >= this.stepIndex('status') && !this.currentRegistration) {
        console.log('Stale localStorage detected (post-submit step but no DB registration). Clearing...');
        this.clearProgress();
        this.step = 0;
        return;
      }
      const shouldResume = await this.showResumeDialog();
      if (!shouldResume) {
        this.step = 0;
        this.resetForm();
        this.status = null;
        this.purchasedNumbers = [];
        this.clearProgress();
      }
    }
  },
  beforeDestroy() {
    if (this.step > 0 && this.step < this.stepIndex('complete')) {
      this.saveProgress();
    }
  },
  methods: {
    ...mapActions('a2p', [
      'submitA2PRegistration',
      'getA2PRegistrationStatus',
      'updateA2PRegistration',
      'getPhoneNumbers',
      'clearRegistration',
      'deleteA2PRegistration',
      'checkSiteDatabase',
      'createSiteDatabase',
    ]),

    resetForm() {
      this.form = {
        businessName: '',
        businessAddress: '',
        ein: '',
        businessType: '',
        website: '',
        country: this.targetSiteCountry || 'US',
        repName: '',
        repTitle: '',
        repEmail: '',
        repPhone: '',
        brandDescription: '',
        sampleMessage: '',
        optInOut: '',
        privacyPolicyURL: '',
        termsOfServiceURL: '',
        optInScreenshotURL: '',
        messageFrequency: 'Up to 8 msgs/month',
        officePhone: '',
        officeEmail: '',
      };
    },

    // Resolve a step key to its index in STEPS. Every place that used to hardcode a
    // step number goes through here so the flow reads by key, not by position.
    stepIndex(key) {
      const i = STEPS.findIndex((s) => s.key === key);
      return i === -1 ? 0 : i;
    },

    canJumpTo(idx) {
      // Allow jumping backwards to any previously visited pre-submit step.
      // Disallow jumping forward (unfilled) or into post-submit flow directly.
      if (idx > this.step) return false;
      if (this.step >= this.stepIndex('status')) return false; // once submitted, sidebar is locked
      return true;
    },

    jumpToStep(idx) {
      if (!this.canJumpTo(idx)) return;
      this.step = idx;
      this.saveProgress();
    },

    navNumFor(i) {
      return i < this.step ? '\u2713' : i + 1;
    },

    async handleNext(data) {
      const key = this.currentStepKey;
      // Steps that emit collected form data on "Continue".
      if (['business', 'representative', 'brand'].includes(key) && data) {
        Object.assign(this.form, data);
        this.saveProgress();
      }
      if (key === 'review') {
        this.submitting = true;
        try {
          const dbCheck = await this.checkSiteDatabase({ targetSiteID: this.effectiveSiteID });
          if (dbCheck.success && !dbCheck.exists) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
              'The site-specific database for this practice does not exist yet. To proceed with A2P registration, we need to create a minimal database with one table so the SMS consent form can load properly.\n\n' +
              'Database to create: ' + dbCheck.databaseName,
              {
                title: 'Site Database Setup Required',
                okVariant: 'primary',
                okTitle: 'Yes, Create Database',
                cancelTitle: 'Cancel',
                centered: true,
                noCloseOnBackdrop: true,
              }
            );
            if (!confirmed) {
              this.submitting = false;
              return;
            }
            const createResult = await this.createSiteDatabase({ targetSiteID: this.effectiveSiteID });
            if (!createResult.success) {
              this.$toast.error(createResult.error || 'Failed to create site database');
              this.submitting = false;
              return;
            }
          }
        } catch (dbError) {
          console.error('Site database check error:', dbError);
        }

        try {
          const result = await this.submitA2PRegistration({
            ...this.form,
            targetSiteID: this.targetSiteID,
          });
          if (result.success) {
            this.status = getRegStatus(result.registration);
            this.step++;
            this.$toast.success('A2P registration submitted successfully! Twilio will review and notify you of the status.');
            if (result.subaccountCreated) {
              this.$toast.info(`Twilio subaccount created: ${result.subaccountSid}`);
            }
          } else {
            this.$toast.error(result.error || 'Registration submission failed');
          }
        } catch (error) {
          console.error('Registration submission error:', error);
          this.$toast.error('An error occurred while submitting registration');
        } finally {
          this.submitting = false;
        }
        return;
      }
      if (key === 'status') {
        await this.loadPhoneNumbers();
        this.step++;
        return;
      }
      if (key === 'phones' && data) {
        if (data.purchasedNumbers) {
          this.purchasedNumbers = data.phoneNumbers || [];
        }
        this.step++;
        this.clearProgress();
        return;
      }
      this.step++;
      this.saveProgress();
    },

    handleBack() {
      if (this.step > 0) {
        this.step--;
        this.saveProgress();
      }
    },

    handleClose() {
      this.$router.push({ name: 'MyAccount' });
    },

    async handleRefresh() {
      try {
        await this.getA2PRegistrationStatus({ targetSiteID: this.targetSiteID });
        if (this.currentRegistration) {
          this.status = getRegStatus(this.currentRegistration);
        }
      } catch (e) {
        console.error('Refresh failed:', e);
      }
    },

    handleImportData(data) {
      if (data) {
        this.form = {
          ...this.form,
          businessName: data.businessName || this.form.businessName,
          businessAddress: data.businessAddress || this.form.businessAddress,
          ein: data.ein || this.form.ein,
          businessType: data.businessType || this.form.businessType,
          website: data.website || this.form.website,
          repName: data.repName || this.form.repName,
          repTitle: data.repTitle || this.form.repTitle,
          repEmail: data.repEmail || this.form.repEmail,
          repPhone: data.repPhone || this.form.repPhone,
          // This map is an explicit whitelist — a key the extractor produces but
          // that isn't listed here is silently dropped. Keep it in sync with
          // `extractedData` in A2PWizardIntro.vue.
          privacyPolicyURL: data.privacyPolicyURL || this.form.privacyPolicyURL,
          termsOfServiceURL: data.termsOfServiceURL || this.form.termsOfServiceURL,
        };
        this.$toast.success('Data imported successfully! Please review and complete any missing fields.');
      }
    },

    async handleStartOver() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Start over? This will delete the current registration and clear all fields.',
        {
          title: 'Start Over',
          okVariant: 'danger',
          okTitle: 'Yes, Start Over',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );
      if (!confirmed) return;

      try {
        if (this.currentRegistration?.ID) {
          await this.deleteA2PRegistration({
            targetSiteID: this.effectiveSiteID,
            registrationId: this.currentRegistration.ID,
          });
        }
        this.resetForm();
        this.status = null;
        this.purchasedNumbers = [];
        this.step = 0;
        this.clearProgress();
        this.clearRegistration();
      } catch (e) {
        console.error('Start over failed:', e);
      }
    },

    async loadPhoneNumbers() {
      try {
        const result = await this.getPhoneNumbers({ targetSiteID: this.targetSiteID });
        this.twilioPhoneNumbers = result?.phoneNumbers || [];
      } catch (e) {
        console.error('Failed to load phone numbers:', e);
        this.twilioPhoneNumbers = [];
      }
    },

    saveProgress() {
      if (!this.storageKey) return;
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          step: this.step,
          form: this.form,
          status: this.status,
        }));
      } catch (e) { /* quota — ignore */ }
    },

    loadProgress() {
      if (!this.storageKey) return false;
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return false;
        const progressData = JSON.parse(raw);
        if (progressData.step !== undefined) this.step = progressData.step;
        if (progressData.form) this.form = { ...this.form, ...progressData.form };
        if (progressData.status) this.status = progressData.status;
        return true;
      } catch (e) {
        return false;
      }
    },

    clearProgress() {
      if (!this.storageKey) return;
      try { localStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }
    },

    async showResumeDialog() {
      return await this.$bvModal.msgBoxConfirm(
        'We found your in-progress A2P registration. Would you like to resume where you left off?',
        {
          title: 'Resume Registration?',
          okVariant: 'primary',
          okTitle: 'Yes, Resume',
          cancelTitle: 'Start Over',
          centered: true,
        }
      );
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
/* Sidebar support styles — prototype sidebar shell */
.a2p-wizard .a2p-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}
/* Collapsed mobile sidebar — keep the brand row compact above the content. */
@media (max-width: 900px) {
  .a2p-wizard .a2p-brand { margin-bottom: 14px; }
}
.a2p-wizard .a2p-brand-mark {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--a2p-accent);
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
}
.a2p-wizard .a2p-brand-name {
  font-weight: 600;
  font-size: 14px;
  letter-spacing: -0.01em;
  color: var(--a2p-fg);
}
.a2p-wizard .a2p-brand-sub {
  font-size: 11px;
  color: var(--a2p-fg-3);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.a2p-wizard .a2p-progress-time {
  margin-top: 8px;
  font-size: 11px;
  color: var(--a2p-fg-3);
  display: flex;
  align-items: center;
  gap: 6px;
}
.a2p-wizard .a2p-progress-time .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--a2p-accent);
  animation: a2p-pulse 2s ease-in-out infinite;
}
@keyframes a2p-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.a2p-wizard .a2p-quick-ref {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--a2p-border);
}
.a2p-wizard .a2p-quick-ref-label {
  font-size: 11px;
  color: var(--a2p-fg-3);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  font-weight: 600;
}
.a2p-wizard .a2p-quick-ref p {
  font-size: 13px;
  color: var(--a2p-fg-2);
  line-height: 1.55;
  margin: 0;
}

.a2p-wizard .a2p-main-wrap {
  /* Holds the current step and owns its outer padding. min-width:0 stops the
     grid's 1fr column from collapsing to its content's min-content width
     (which would render long words one-per-line). */
  min-width: 0;
  width: 100%;
  padding: 40px clamp(20px, 4vw, 48px) 80px;
}
/* Every step's root is now a bare .a2p-main — the wrap owns the padding, so zero
   the step's own padding and center its content column. */
.a2p-wizard .a2p-main-wrap > .a2p-main {
  padding: 0;
  margin: 0 auto;
}
</style>
